"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { authFetch } from "@/lib/api";
import { submitOnboardingGoal } from "./onboardingGoal";
import { LIFE_AREA_COLORS } from "@/lib/lifeAreas";

// dogfood round-2 M1 fix: persist mid-onboarding state to
// localStorage so a refresh mid-flow doesn't wipe everything the
// user just typed. Cleared on successful complete (in the patch
// success path) AND when the user signs out (separate concern).
const ONBOARDING_DRAFT_KEY = "de_onboarding_draft_v1";

interface OnboardingDraft {
  step: number;
  firstThought: string;
  goalText: string;
  selectedAreas: string[];
  goalArea: string | null;
  tone: string;
  wakeTime: string;
  windDownTime: string;
  focusTime: string;
  recurringBlocker: string;
  decisionRulePicks: string[];
  recurringAmbition: string;
}

function readOnboardingDraft(): Partial<OnboardingDraft> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ONBOARDING_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch {
    // corrupt JSON — drop silently
  }
  return null;
}

function clearOnboardingDraft(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ONBOARDING_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

interface Props {
  userName: string;
  onComplete: () => void;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Phase 11-1: chip values for the focus-time question. The user-facing
// strings ARE the persisted memory_records.content for memory_type
// "preference" — short labels intentionally so Coach / Planner prompt
// injection later (11-4) can quote them inline without paraphrasing.
const FOCUS_TIME_CHIPS = ["Morning", "Afternoon", "Evening", "It varies"] as const;

// Phase 11-1: chip values for the decision-rule question. Multi-select —
// the chosen labels are joined with ", " and stored as the content for
// memory_type "decision_rule". Locked to a short fixed list so future
// 11-4 prompt injection has predictable vocabulary.
const DECISION_RULE_CHIPS = [
  "Freedom",
  "Growth",
  "Relationships",
  "Money",
  "Stability",
  "Health",
] as const;

const LIFE_AREAS = [
  { id: "health", label: "Health & Fitness", emoji: "🏃" },
  { id: "career", label: "Career & Work", emoji: "💼" },
  { id: "learning", label: "Learning & Growth", emoji: "📖" },
  { id: "relationships", label: "Relationships", emoji: "🤝" },
  { id: "finance", label: "Finance & Money", emoji: "💰" },
] as const;

// dogfood round-2 C1 fix: labels were miswired to values — e.g.
// "Calm & direct" used to map to `direct_mentor` (whose Settings
// label is "Straight to the point") and "Minimal — just show me
// the tasks" used to map to `gentle_friend` (whose Settings label
// is "Soft and understanding"). Picking onboarding tone therefore
// saved a different tone than the user thought they chose, and
// Settings → Tone surfaced the contradiction.
//
// Align onboarding labels with the canonical Settings ToneSection
// labels so the same value carries the same descriptor everywhere.
// Also add the previously-missing `hype_partner` option so all four
// backend-supported tones are pickable at onboarding time.
const TONE_OPTIONS = [
  {
    value: "warm_coach",
    label: "Warm coach — encouraging and supportive",
    emoji: "🤗",
  },
  {
    value: "direct_mentor",
    label: "Direct mentor — straight to the point",
    emoji: "🚀",
  },
  {
    value: "gentle_friend",
    label: "Gentle friend — soft and understanding",
    emoji: "🌴",
  },
  {
    value: "hype_partner",
    label: "Hype partner — energetic and motivating",
    emoji: "🎉",
  },
] as const;

export default function Onboarding({ userName, onComplete }: Props) {
  // dogfood round-2 M1: lazy-init from localStorage so refresh
  // mid-flow doesn't wipe the user's progress.
  const draftRef = useRef<Partial<OnboardingDraft> | null>(
    typeof window !== "undefined" ? readOnboardingDraft() : null
  );
  const draft = draftRef.current ?? {};

  const [step, setStep] = useState<Step>(
    (draft.step as Step) || 1
  );
  const [firstThought, setFirstThought] = useState(draft.firstThought ?? "");
  const [goalText, setGoalText] = useState(draft.goalText ?? "");
  const [selectedAreas, setSelectedAreas] = useState<string[]>(
    draft.selectedAreas ?? []
  );
  // Which life area this onboarding goal lands in. Defaults to the
  // first picked area; the goal step lets the user override before
  // committing. Only used when at least one area is selected.
  const [goalArea, setGoalArea] = useState<string | null>(draft.goalArea ?? null);
  const [tone, setTone] = useState(draft.tone ?? "warm_coach");
  const [wakeTime, setWakeTime] = useState(draft.wakeTime ?? "07:00");
  const [windDownTime, setWindDownTime] = useState(
    draft.windDownTime ?? "21:00"
  );
  const [submitting, setSubmitting] = useState(false);
  const [goalError, setGoalError] = useState<string | null>(null);
  const [capturedThought, setCapturedThought] = useState(false);

  // Phase 11-1: optional calibration step (Step 6). Each field is
  // independently skippable — empty / unselected values are excluded
  // from the PATCH preference_seeds payload below, so the backend's
  // per-question skip path stays driven by client intent.
  const [focusTime, setFocusTime] = useState<string>(draft.focusTime ?? "");
  const [recurringBlocker, setRecurringBlocker] = useState<string>(
    draft.recurringBlocker ?? ""
  );
  const [decisionRulePicks, setDecisionRulePicks] = useState<string[]>(
    draft.decisionRulePicks ?? []
  );
  const [recurringAmbition, setRecurringAmbition] = useState<string>(
    draft.recurringAmbition ?? ""
  );

  // Persist draft on any change so a refresh resumes where the user
  // left off. Cleared in the success path of the final PATCH.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload: OnboardingDraft = {
      step,
      firstThought,
      goalText,
      selectedAreas,
      goalArea,
      tone,
      wakeTime,
      windDownTime,
      focusTime,
      recurringBlocker,
      decisionRulePicks,
      recurringAmbition,
    };
    try {
      window.localStorage.setItem(
        ONBOARDING_DRAFT_KEY,
        JSON.stringify(payload)
      );
    } catch {
      /* quota / disabled storage — best-effort */
    }
  }, [
    step,
    firstThought,
    goalText,
    selectedAreas,
    goalArea,
    tone,
    wakeTime,
    windDownTime,
    focusTime,
    recurringBlocker,
    decisionRulePicks,
    recurringAmbition,
  ]);

  const next = useCallback(() => {
    setStep((s) => Math.min(s + 1, 7) as Step);
  }, []);

  const toggleDecisionRule = useCallback((label: string) => {
    setDecisionRulePicks((prev) =>
      prev.includes(label) ? prev.filter((p) => p !== label) : [...prev, label]
    );
  }, []);

  const toggleArea = useCallback((id: string) => {
    setSelectedAreas((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  }, []);

  // Step 1: Quick win — capture first thought
  const handleCaptureThought = useCallback(async () => {
    if (!firstThought.trim()) return;
    setSubmitting(true);
    try {
      await authFetch("/api/thoughts", {
        method: "POST",
        body: JSON.stringify({ text: firstThought.trim(), source: "onboarding" }),
      });
      setCapturedThought(true);
      setTimeout(next, 800);
    } catch {
      // Still proceed even if capture fails
      next();
    } finally {
      setSubmitting(false);
    }
  }, [firstThought, next]);

  // Step 3: Goal setup — creates a goal AND a starter task. Runs
  // AFTER life-area selection so the new goal lands in the area the
  // user just picked instead of always defaulting to "personal".
  const handleGoalSubmit = useCallback(async () => {
    if (!goalText.trim()) {
      next();
      return;
    }
    setSubmitting(true);
    setGoalError(null);
    // submitOnboardingGoal checks res.ok and returns a discriminated result
    // instead of swallowing failures (BUG-005 / BUG-002). The starter task is
    // best-effort: if the goal saved but the task POST failed, we still advance
    // (the goal is the primary artifact and re-submitting would risk a duplicate
    // goal). Only a goal-creation failure blocks + surfaces an error to retry.
    const result = await submitOnboardingGoal(authFetch, {
      title: goalText.trim(),
      category: goalArea || selectedAreas[0] || "personal",
      goalText: goalText.trim(),
    });
    setSubmitting(false);

    if (result.status === "goal-failed") {
      setGoalError("Couldn't save your goal — please try again.");
      return; // do NOT advance: the goal was not created, safe to retry
    }
    next();
  }, [goalText, selectedAreas, goalArea, next]);

  // Step 3: Tone
  const handleToneSelect = useCallback(
    (value: string) => {
      setTone(value);
      next();
    },
    [next]
  );

  // Step 4: Schedule
  const handleScheduleSubmit = useCallback(() => {
    next();
  }, [next]);

  // Step 5: Complete
  const handleComplete = useCallback(async () => {
    setSubmitting(true);
    try {
      // dogfood round-2 C4 fix: previously when the user picked
      // an overnight window (e.g. wake=23:00 wind=03:00) the math
      // silently clamped to startHour=23, endHour=24 — losing the
      // entire post-midnight working block. Backend schema
      // (OnboardingUpdate) caps day_end_hour at 24, so we cannot
      // represent overnight windows on the wire. Honest fallback:
      // detect the overnight case + log it, then submit a safe
      // same-day default (8..20) so the user isn't left with a
      // broken 1-hour window. The user can later widen via
      // Settings → Working Hours, where the UI supports the
      // explicit choice they want.
      const [wakeH, wakeM] = wakeTime.split(":").map(Number);
      const [windH, windM] = windDownTime.split(":").map(Number);
      const startHourRaw = Number.isFinite(wakeH)
        ? Math.max(0, Math.min(23, wakeH))
        : 8;
      const windCeil = Number.isFinite(windH)
        ? windH + (windM > 0 ? 1 : 0)
        : 20;
      const overnight = windCeil <= startHourRaw;
      const startHour = overnight ? 8 : startHourRaw;
      const endHour = overnight
        ? 20
        : Math.max(startHour + 1, Math.min(24, windCeil));
      if (overnight) {
        // Surface in the console so support has a breadcrumb;
        // the UI silently falls back so onboarding doesn't dead-
        // end on an unsupported window. Next iteration: replace
        // the silent fallback with an inline error + block submit.
        console.warn(
          "[onboarding] overnight working window not yet supported " +
            "(wake=" + wakeTime + " wind=" + windDownTime + "); " +
            "falling back to 8:00–20:00. User can widen later in Settings."
        );
      }

      // Phase 11-1: build preference_seeds from the optional calibration
      // step. Only include keys whose user-provided value is non-empty,
      // so the backend's `if (raw_content or "").strip()` skip logic
      // never receives a no-op row. decision_rule joins selected chips
      // with ", " to land a single human-readable string (also keeps the
      // memory_type's content scannable for 11-4 prompt injection).
      const seeds: Record<string, string> = {};
      const focusTrim = focusTime.trim();
      if (focusTrim) seeds.preference = focusTrim;
      const blockerTrim = recurringBlocker.trim();
      if (blockerTrim) seeds.recurring_blocker = blockerTrim;
      if (decisionRulePicks.length > 0) {
        seeds.decision_rule = decisionRulePicks.join(", ");
      }
      const ambitionTrim = recurringAmbition.trim();
      if (ambitionTrim) seeds.recurring_ambition = ambitionTrim;

      // dogfood round-2 C4 fix: previously this payload also auto-
      // populated quiet_hours_start/end as the inverse of working
      // hours, with no onboarding UI to confirm. The user reported
      // discovering they "had quiet hours set" they never knew
      // about. Quiet hours are a separate concept (when DecisionEase
      // suppresses push notifications) and should be opted into
      // explicitly via Settings → Quiet Hours. Onboarding now only
      // sets what the UI actually exposed: tone + working window +
      // life areas + memory seeds.
      const patchBody: Record<string, unknown> = {
        tone_preference: tone,
        day_start_hour: startHour,
        day_end_hour: endHour,
        life_areas: selectedAreas,
        onboarding_complete: true,
      };
      if (Object.keys(seeds).length > 0) {
        patchBody.preference_seeds = seeds;
      }
      // Energy zones: the focus-time chip also persists as a chronotype the
      // SCHEDULER reads (users.chronotype → energy_zones.py), not only a
      // memory seed — previously "Evening" never reached block placement
      // (baseline drive 2026-06-10).
      const chronotypeByChip: Record<string, string> = {
        Morning: "early_bird",
        Afternoon: "midday",
        Evening: "night_owl",
        "It varies": "varies",
      };
      if (focusTrim && chronotypeByChip[focusTrim]) {
        patchBody.chronotype = chronotypeByChip[focusTrim];
      }

      await authFetch("/api/settings/onboarding", {
        method: "PATCH",
        body: JSON.stringify(patchBody),
      });
    } catch {
      // Complete anyway
    } finally {
      // dogfood round-2 M1 fix: clear the in-progress draft so a
      // future visit doesn't restore stale answers. Only fires on
      // the final PATCH path — refresh mid-flow still resumes from
      // the draft (the whole point of the persistence).
      clearOnboardingDraft();
      setSubmitting(false);
      onComplete();
    }
  }, [
    tone,
    wakeTime,
    windDownTime,
    selectedAreas,
    focusTime,
    recurringBlocker,
    decisionRulePicks,
    recurringAmbition,
    onComplete,
  ]);

  return (
    <div
      data-testid="onboarding-container"
      className="onboarding-container min-h-[calc(100vh-52px)] flex items-center justify-center px-4"
    >
      <div className="w-full max-w-md">
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-8">
          {[1, 2, 3, 4, 5, 6, 7].map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                s === step
                  ? "bg-forest w-6"
                  : s < step
                  ? "bg-forest/40"
                  : "bg-border/50"
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Quick win */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">💭</span>
              </div>
              <h2 className="text-xl font-semibold text-text mb-2">
                Welcome, {userName}!
              </h2>
              {/* dogfood round-2 H2 fix: previous copy said "A goal,
                  a decision..." which led testers to type a goal here
                  in step 1. Step 1 is a QUICK CAPTURE (saved as a
                  Thought) — the dedicated goal step is step 3. The
                  old wording made step 1 feel like the goal step,
                  then step 3 looked like a duplicate prompt and the
                  goal never got persisted as an actual Goal row.
                  Reframe step 1 as a plain capture / brain-dump so
                  step 3's goal flow has a clean role. */}
              <p className="text-sm text-muted mb-6 leading-relaxed">
                Before we set anything up — give me a quick brain dump.
                What&apos;s the loudest open loop in your head right now? (We&apos;ll
                set goals on the next step.)
              </p>
              <textarea
                value={firstThought}
                onChange={(e) => setFirstThought(e.target.value)}
                placeholder="What's on your mind..."
                aria-label="What's on your mind"
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-border
                           bg-card text-text
                           focus:border-forest focus:outline-none
                           resize-none text-sm"
              />
              {capturedThought && (
                <motion.p
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-sm text-forest  mt-2"
                >
                  Captured! ✓
                </motion.p>
              )}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={next}
                  className="flex-1 px-5 py-2.5 rounded-full text-sm font-medium
                             text-muted hover:bg-secondary"
                >
                  Skip
                </button>
                <button
                  onClick={handleCaptureThought}
                  disabled={!firstThought.trim() || submitting}
                  className="flex-1 px-5 py-2.5 rounded-full text-sm font-medium
                             bg-forest text-white hover:bg-forest/90 transition-colors
                             disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Saving..." : "Capture it"}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Goal setup (now after Life Areas so we can tag
              the new goal with the area the user just selected) */}
          {step === 3 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🎯</span>
              </div>
              <h2 className="text-xl font-semibold text-text mb-2">
                What are you working toward?
              </h2>
              <p className="text-sm text-muted mb-6 leading-relaxed">
                Could be career, health, learning — anything. Just describe it naturally.
              </p>
              <textarea
                value={goalText}
                onChange={(e) => setGoalText(e.target.value)}
                placeholder="e.g., Get better at public speaking, Run a 5K, Learn Python..."
                aria-label="Describe your goal"
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-border
                           bg-card text-text
                           focus:border-forest focus:outline-none
                           resize-none text-sm"
              />
              {selectedAreas.length > 0 && (
                <div className="mt-3 text-left">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                    This goal belongs to
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {selectedAreas.map((areaId) => {
                      const meta = LIFE_AREAS.find((a) => a.id === areaId);
                      if (!meta) return null;
                      const active = (goalArea ?? selectedAreas[0]) === areaId;
                      return (
                        <button
                          key={areaId}
                          type="button"
                          data-testid={`onboarding-goal-area-${areaId}`}
                          onClick={() => setGoalArea(areaId)}
                          className={`rounded-full border px-3 py-1 text-[11px] font-medium transition-colors ${
                            active
                              ? "border-forest bg-forest text-white"
                              : "border-border bg-card text-text-light hover:border-forest/40"
                          }`}
                        >
                          {meta.emoji} {meta.label.split(" & ")[0]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* dogfood round-2 M2 fix: previously the right
                  button rendered as "Continue" with empty input and
                  acted exactly like "Skip" (same outcome — advance
                  without saving anything), which made testers say
                  "two buttons that do the same thing". Now: the
                  primary button is "Set goal" and only enables when
                  goalText has content. Empty-input path is "Skip"
                  alone, which is honest about what happens. */}
              {goalError && (
                <p
                  role="alert"
                  className="mt-3 text-sm text-amber"
                >
                  {goalError}
                </p>
              )}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={next}
                  className="flex-1 px-5 py-2.5 rounded-full text-sm font-medium
                             text-muted hover:bg-secondary"
                >
                  Skip
                </button>
                <button
                  onClick={handleGoalSubmit}
                  disabled={submitting || !goalText.trim()}
                  className="flex-1 px-5 py-2.5 rounded-full text-sm font-medium
                             bg-forest text-white hover:bg-forest/90 transition-colors
                             disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Saving..." : "Set goal"}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Life Areas (moved before Goal so the goal can
              be created with the user's chosen primary area) */}
          {step === 2 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🌱</span>
              </div>
              <h2 className="text-xl font-semibold text-text mb-2">
                What matters most to you?
              </h2>
              <p className="text-sm text-muted mb-6 leading-relaxed">
                Pick the areas you want to focus on. We&apos;ll use these
                to organize your goals and track progress.
              </p>
              <div className="space-y-2.5">
                {LIFE_AREAS.map((area) => {
                  const selected = selectedAreas.includes(area.id);
                  // dogfood round-2 L3 fix: the picker rendered all
                  // 5 areas in identical forest-green active state,
                  // contradicting the rest of the app where every
                  // life area has its own color (LIFE_AREA_COLORS).
                  // Surface each area's canonical color as a small
                  // leading dot so users can recognise the area at
                  // a glance — same visual language as BlockEditModal
                  // / QueuePeek / chat memory_refs / Reflect chips.
                  const areaColor = LIFE_AREA_COLORS[area.id];
                  return (
                    <button
                      key={area.id}
                      onClick={() => toggleArea(area.id)}
                      aria-pressed={selected}
                      className={`w-full px-5 py-3.5 rounded-xl border text-left flex items-center gap-3
                                  transition-all duration-200 ${
                                    selected
                                      ? "border-forest bg-forest/5 ring-1 ring-forest/20"
                                      : "border-border hover:border-forest/30"
                                  }`}
                    >
                      {areaColor && (
                        <span
                          aria-hidden="true"
                          className="inline-block h-2 w-2 shrink-0 rounded-full"
                          style={{ background: areaColor }}
                        />
                      )}
                      <span className="text-lg">{area.emoji}</span>
                      <span className="text-sm font-medium text-text">{area.label}</span>
                      {selected && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="ml-auto text-forest">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={next}
                className="mt-6 px-8 py-2.5 rounded-full text-sm font-medium
                           bg-forest text-white hover:bg-forest/90 transition-colors"
              >
                {selectedAreas.length > 0
                  ? `Continue with ${selectedAreas.length} area${selectedAreas.length > 1 ? "s" : ""}`
                  : "Skip for now"}
              </button>
            </motion.div>
          )}

          {/* Step 4: Tone preference */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🎨</span>
              </div>
              <h2 className="text-xl font-semibold text-text mb-2">
                How should I talk to you?
              </h2>
              <p className="text-sm text-muted mb-6 leading-relaxed">
                You can always change this later in Settings.
              </p>
              <div className="space-y-3">
                {TONE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleToneSelect(opt.value)}
                    className={`w-full px-5 py-4 rounded-xl border text-left flex items-center gap-3
                                transition-all duration-200 ${
                                  tone === opt.value
                                    ? "border-forest bg-forest/5"
                                    : "border-border hover:border-forest/30 hover:border-primary/30"
                                }`}
                  >
                    <span className="text-xl">{opt.emoji}</span>
                    <span className="text-sm font-medium text-text">
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 5: Schedule */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⏰</span>
              </div>
              <h2 className="text-xl font-semibold text-text mb-2">
                When do you start and wind down?
              </h2>
              <p className="text-sm text-muted mb-6 leading-relaxed">
                I&apos;ll use this for your morning brief and evening check-in.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between px-4">
                  <label htmlFor="onboarding-wake-time" className="text-sm text-text-light">
                    Wake up
                  </label>
                  <input
                    id="onboarding-wake-time"
                    type="time"
                    step={3600}
                    value={wakeTime}
                    onChange={(e) => setWakeTime(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-border
                               bg-card text-text
                               text-sm focus:outline-none focus:border-forest"
                  />
                </div>
                <div className="flex items-center justify-between px-4">
                  <label htmlFor="onboarding-wind-down" className="text-sm text-text-light">
                    Wind down
                  </label>
                  <input
                    id="onboarding-wind-down"
                    type="time"
                    step={3600}
                    value={windDownTime}
                    onChange={(e) => setWindDownTime(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-border
                               bg-card text-text
                               text-sm focus:outline-none focus:border-forest"
                  />
                </div>
              </div>
              <button
                onClick={handleScheduleSubmit}
                className="mt-6 px-8 py-2.5 rounded-full text-sm font-medium
                           bg-forest text-white hover:bg-forest/90 transition-colors"
              >
                Continue
              </button>
            </motion.div>
          )}

          {/* Step 6: Optional calibration — Phase 11-1 preference seeds.
              Every field is independently skippable; the whole step can
              be skipped with "Skip for now". Empty fields are excluded
              from the PATCH preference_seeds payload. */}
          {step === 6 && (
            <motion.div
              key="step6-calibration"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              data-testid="onboarding-step-calibration"
            >
              <div className="mb-5">
                <h2 className="text-xl font-semibold text-text mb-2">
                  Help the AI start with a few notes about how you work
                </h2>
                <p className="text-sm text-muted leading-relaxed">
                  All optional — answer what feels useful, skip what doesn&apos;t.
                </p>
              </div>

              <div className="space-y-5">
                {/* preference — focus time chips */}
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    When do you usually focus best?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {FOCUS_TIME_CHIPS.map((chip) => {
                      const active = focusTime === chip;
                      return (
                        <button
                          key={chip}
                          type="button"
                          data-testid={`calibration-focus-${chip
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                          onClick={() => setFocusTime(active ? "" : chip)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            active
                              ? "bg-forest text-white border-forest"
                              : "bg-card text-text-light border-border hover:border-forest/40"
                          }`}
                        >
                          {chip}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* recurring_blocker — textarea */}
                <div>
                  <label
                    htmlFor="calibration-blocker"
                    className="block text-sm font-medium text-text mb-2"
                  >
                    What most often gets in the way when you try to start?
                  </label>
                  <textarea
                    id="calibration-blocker"
                    data-testid="calibration-blocker-input"
                    value={recurringBlocker}
                    onChange={(e) => setRecurringBlocker(e.target.value)}
                    placeholder="Optional — a sentence is plenty"
                    maxLength={300}
                    rows={2}
                    className="w-full px-3 py-2 rounded-xl text-sm border border-border bg-card
                      focus:outline-none focus:ring-2 focus:ring-forest/30 resize-none"
                  />
                </div>

                {/* decision_rule — multi-chips */}
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    {/* dogfood round-2 M3 fix: copy was singular
                        ("what matters most") but UI allows multi-
                        select. Reword to "what matters to you" so
                        copy + behavior match. */}
                    When making a big decision, what matters to you?
                  </label>
                  <p className="text-[11.5px] text-text-light mb-2">
                    Pick any number — these get saved as your decision rules.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DECISION_RULE_CHIPS.map((chip) => {
                      const active = decisionRulePicks.includes(chip);
                      return (
                        <button
                          key={chip}
                          type="button"
                          // dogfood round-2 L2 fix: chips visually
                          // toggle but exposed no aria-pressed. Screen
                          // readers couldn't tell what was selected.
                          aria-pressed={active}
                          data-testid={`calibration-decision-${chip.toLowerCase()}`}
                          onClick={() => toggleDecisionRule(chip)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            active
                              ? "bg-forest text-white border-forest"
                              : "bg-card text-text-light border-border hover:border-forest/40"
                          }`}
                        >
                          {chip}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* recurring_ambition — textarea */}
                <div>
                  <label
                    htmlFor="calibration-ambition"
                    className="block text-sm font-medium text-text mb-2"
                  >
                    What&apos;s something you keep meaning to do?
                  </label>
                  <textarea
                    id="calibration-ambition"
                    data-testid="calibration-ambition-input"
                    value={recurringAmbition}
                    onChange={(e) => setRecurringAmbition(e.target.value)}
                    placeholder="Optional — a goal you've been postponing"
                    maxLength={300}
                    rows={2}
                    className="w-full px-3 py-2 rounded-xl text-sm border border-border bg-card
                      focus:outline-none focus:ring-2 focus:ring-forest/30 resize-none"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-between items-center">
                <button
                  type="button"
                  data-testid="calibration-skip"
                  onClick={() => {
                    setFocusTime("");
                    setRecurringBlocker("");
                    setDecisionRulePicks([]);
                    setRecurringAmbition("");
                    next();
                  }}
                  className="text-sm text-muted hover:text-text transition-colors"
                >
                  Skip for now
                </button>
                <button
                  type="button"
                  data-testid="calibration-continue"
                  onClick={next}
                  className="px-6 py-2 rounded-full text-sm font-medium
                    bg-forest text-white hover:bg-forest/90 transition-colors"
                >
                  Continue
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 7: Done */}
          {step === 7 && (
            <motion.div
              key="step7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="w-16 h-16 rounded-2xl bg-forest/10 flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">✨</span>
              </div>
              <h2 className="text-xl font-semibold text-text mb-2">
                All set!
              </h2>
              <p className="text-sm text-muted mb-6 leading-relaxed">
                Your first task is waiting. Let&apos;s get started.
              </p>
              <button
                onClick={handleComplete}
                disabled={submitting}
                className="px-8 py-2.5 rounded-full text-sm font-medium
                           bg-forest text-white hover:bg-forest/90 transition-colors
                           disabled:opacity-40"
              >
                {submitting ? "Setting up..." : "See your Focus"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
